import { NavigateFunction } from 'react-router-dom'
import {
  dataTypes,
  MAX_PAGE_SIZE,
  SearchReviewType,
  searchReviewTypes,
  urlList,
} from '../constants/constants'
import i18n from '../i18n/i18n'
import {
  SearchReview,
  SearchSource,
} from '../store/reducers/searchReviewReducer'
import { SearchMethod } from '../types/generalTypes'
import apiService from './apiService'

interface SearchParameters {
  dataType: string[]
  date: string
  language: string
  searchMethod: string
  searchQuery: string
  source: string
  lastModifiedDateStart?: string
  lastModifiedDateEnd?: string
}

interface SearchBody {
  searchDescription: string
  searchParameters: SearchParameters
}

interface SaveSearchQueryInSearchReviewProps {
  navigate: NavigateFunction
  searchQuery?: string
  searchMethod?: SearchMethod
  sourceId?: string
  period?: string
  title?: string
  searchReviewId?: string
  reviewType?: SearchReviewType
  lastModifiedDateStart?: string
  lastModifiedDateEnd?: string
}

const createSearchReviewFromSearchQuery = async ({
  navigate,
  searchQuery,
  searchMethod,
  sourceId,
  period,
  title,
  reviewType,
  lastModifiedDateStart,
  lastModifiedDateEnd,
}: SaveSearchQueryInSearchReviewProps) => {
  const searchBody: SearchBody = {
    searchDescription: searchQuery || '',
    searchParameters: {
      dataType: [dataTypes.DOCUMENT],
      date: period || '',
      language: i18n.language,
      searchMethod: searchMethod || '',
      searchQuery: searchQuery || '',
      source: sourceId || '',
    },
  }

  const params = {
    searchBody,
    title,
    reviewType: reviewType || searchReviewTypes.LITERATURE_REVIEW,
  }

  if (lastModifiedDateStart) {
    searchBody.searchParameters.lastModifiedDateStart = lastModifiedDateStart
  }
  if (lastModifiedDateEnd) {
    searchBody.searchParameters.lastModifiedDateEnd = lastModifiedDateEnd
  }

  return apiService
    .saveItem(urlList.SEARCH_REVIEWS, navigate, params)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios save search error', error)
      throw error
    })
}

interface GetSearchReviewListProps {
  page?: number
  pageSize?: number
  ownedOnly?: boolean
}

const getSearchReviewList = async ({
  page,
  pageSize,
  ownedOnly,
}: GetSearchReviewListProps) => {
  const hasPagination = !!page && !!pageSize
  const urlPaginated = hasPagination
    ? `${urlList.SEARCH_REVIEWS}?page=${page}&pageSize=${pageSize}`
    : `${urlList.SEARCH_REVIEWS}?page=1&pageSize=${MAX_PAGE_SIZE}`
  return apiService
    .fetchItems(`${urlPaginated}${ownedOnly ? '&ownedOnly=true' : ''}`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}
interface GetSearchReviewListByIdsProps {
  searchReviewIds: string[]
}

const getSearchReviewListByIds = async ({
  searchReviewIds,
}: GetSearchReviewListByIdsProps) => {
  return apiService
    .fetchItems(`${urlList.SEARCH_REVIEWS}/ids=${searchReviewIds.join(',')}`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

const saveSearchQueryInSearchReview = async ({
  navigate,
  searchQuery,
  searchMethod,
  sourceId,
  period,
  lastModifiedDateStart,
  lastModifiedDateEnd,
  searchReviewId,
}: SaveSearchQueryInSearchReviewProps) => {
  const searchBody: SearchBody = {
    searchDescription: searchQuery || '',
    searchParameters: {
      dataType: [dataTypes.DOCUMENT],
      date: period || '',
      language: i18n.language,
      searchMethod: searchMethod || '',
      searchQuery: searchQuery || '',
      source: sourceId || '',
    },
  }

  const params = {
    searchBody,
    title: undefined,
  }

  if (lastModifiedDateStart) {
    searchBody.searchParameters.lastModifiedDateStart = lastModifiedDateStart
  }
  if (lastModifiedDateEnd) {
    searchBody.searchParameters.lastModifiedDateEnd = lastModifiedDateEnd
  }

  return apiService
    .updateItem(
      `${urlList.SEARCH_REVIEWS}/${searchReviewId}/newQuery`,
      navigate,
      params
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios save search error', error)
      throw error
    })
}
// }

interface SaveSearchQueryInclusion {
  searchReviewId: string
  includedSearchResults: string[]
  recordIds: string | string[]
  navigate: NavigateFunction
}

const saveSearchQueryInclusion = async ({
  searchReviewId,
  includedSearchResults,
  recordIds,
  navigate,
}: SaveSearchQueryInclusion) => {
  const ids = typeof recordIds === 'string' ? [recordIds] : recordIds
  let includedList = includedSearchResults

  ids.forEach((id) => {
    if (includedList?.includes(id)) {
      includedList = includedList.filter((searchResult) => searchResult !== id)
    } else {
      includedList = [...includedList, id]
    }
  })

  const params = {
    includedSearchResults: includedList,
  }

  return apiService
    .updateItem(`${urlList.SEARCH_REVIEWS}/${searchReviewId}`, navigate, params)
    .then((response) => {
      return response
    })
    .catch((error) => {
      apiService.notificationError(error)
      console.error('axios save search error', error)
      throw error
    })
}

interface UpdateSearchReviewDetailsProps {
  title: string
  description: string
  searchReviewId: string
  navigate: NavigateFunction
}

const updateSearchReviewDetails = async ({
  title,
  description,
  searchReviewId,
  navigate,
}: UpdateSearchReviewDetailsProps) => {
  const params = {
    title,
    description,
  }

  return apiService
    .updateItem(`${urlList.SEARCH_REVIEWS}/${searchReviewId}`, navigate, params)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios search error', error)
      throw error
    })
}

interface DeleteSearchReviewProps {
  searchReviewId: string
  navigate: NavigateFunction
}

const deleteSearchReview = async ({
  searchReviewId,
  navigate,
}: DeleteSearchReviewProps) => {
  return apiService
    .deleteItem(`${urlList.SEARCH_REVIEWS}/${searchReviewId}`, navigate)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios delete error', error)
      throw error
    })
}

const getSearchQuerySourceId = (
  searchQuery: string | SearchSource | undefined
) => {
  if (typeof searchQuery === 'string') {
    return searchQuery
  } else if (searchQuery !== undefined) {
    return searchQuery.id
  }
  return ''
}

const exportSearchReviewList = async () => {
  return apiService
    .fetchItems(`${urlList.SEARCH_REVIEWS}/csv`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface ExportSearchReviewToCsvProps {
  searchReviewId: string
}

const exportSearchReviewToCsv = async ({
  searchReviewId,
}: ExportSearchReviewToCsvProps) => {
  return apiService
    .fetchItems(`${urlList.SEARCH_REVIEWS}/${searchReviewId}/csv`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface ExportSearchReviewSearchQueryToCsvProps {
  searchReviewId: string
  searchQueryId: string
}

const exportSearchReviewSearchQueryToCsv = async ({
  searchReviewId,
  searchQueryId,
}: ExportSearchReviewSearchQueryToCsvProps) => {
  return apiService
    .fetchItems(
      `${urlList.SEARCH_REVIEWS}/${searchReviewId}/searchId=${searchQueryId}/csv`
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface SummarizeSearchQueryProps {
  searchReviewId: string
  resultId: string
}

const summarizeSearchQueryResult = ({
  searchReviewId,
  resultId,
}: SummarizeSearchQueryProps) => {
  return apiService
    .saveItem(
      `${urlList.SEARCH_REVIEWS}/${searchReviewId}/resultId=${resultId}/summary/process`
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

const getSearchReview = async (searchReviewId: string) => {
  return apiService
    .fetchItems(`${urlList.SEARCH_REVIEWS}/${searchReviewId}`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface GetSearchReviewQueryByIdProps {
  searchReviewId: string
  searchQueryId: string
}

const getSearchReviewQueryById = async ({
  searchReviewId,
  searchQueryId,
}: GetSearchReviewQueryByIdProps) => {
  return apiService
    .fetchItems(
      `${urlList.SEARCH_REVIEWS}/${searchReviewId}/searchId=${searchQueryId}`
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface FindSearchQueryInSearchReviewList {
  searchReviewList?: SearchReview[]
  searchQueryId?: string
}

const findSearchQueryInSearchReviewList = ({
  searchReviewList,
  searchQueryId,
}: FindSearchQueryInSearchReviewList) => {
  if (!searchReviewList?.length) return undefined

  const allQueries = searchReviewList.flatMap((review) => review.searchQueries)
  if (!allQueries.length) return undefined

  const searchQuery = allQueries.find((query) => query?.id === searchQueryId)
  if (!searchQuery) return undefined

  return searchQuery
}

interface DeleteSearchReviewQueryProps {
  searchReviewId: string
  searchQueryId: string
  navigate: NavigateFunction
}

const deleteSearchReviewQuery = async ({
  searchReviewId,
  searchQueryId,
  navigate,
}: DeleteSearchReviewQueryProps) => {
  return apiService
    .deleteItem(
      `${urlList.SEARCH_REVIEWS}/${searchReviewId}/searchId=${searchQueryId}`,
      navigate
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios delete error', error)
      throw error
    })
}

export const searchReviewService = {
  createSearchReviewFromSearchQuery,
  deleteSearchReview,
  getSearchReviewList,
  getSearchReviewListByIds,
  exportSearchReviewList,
  exportSearchReviewSearchQueryToCsv,
  exportSearchReviewToCsv,
  findSearchQueryInSearchReviewList,
  getSearchQuerySourceId,
  getSearchReview,
  getSearchReviewQueryById,
  saveSearchQueryInclusion,
  saveSearchQueryInSearchReview,
  summarizeSearchQueryResult,
  updateSearchReviewDetails,
  deleteSearchReviewQuery,
}

export default searchReviewService
